import React, { useState, useEffect, useMemo } from "react";
import SublineApi from "../../services/SublineApi";
import { useTranslation } from 'react-i18next';

// Design System
import Input from "../../components/Input/Input";
import { Button } from "../../components/Button/Button";
import Modal from "../../components/Modal/Modal";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";

interface SublinesOption {
    value: string;
    label: string;
}

interface StationDialogProps {
    visible: boolean;
    onHide: () => void;
    onCreate: (name: string, description: string, sublineId: string) => void;
    station: { id: string; name: string; description: string,  sublineId: string;} | null;
}

const StationDialog: React.FC<StationDialogProps> = ({ visible, onHide, onCreate, station }) => {
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [sublines, setSublines] = useState<SublinesOption[]>([]);
    const [subline, setSubline] = useState<string>("");
    const [errors, setErrors] = useState<{ name?: string; description?: string; subline?: string }>({});
    const [showConfirmCancel, setShowConfirmCancel] = useState(false);

    const { t: translateGeneral } = useTranslation("general");
    const { t: translateForm } = useTranslation("form");

    const isDirty = useMemo(() => {
        if (station) {
            return name !== station.name || description !== station.description || subline !== station.sublineId;
        }
        return name !== "" || description !== "" || subline !== "";
    }, [name, description, subline, station]);

    // Preenche os campos se a prop station estiver disponível
    useEffect(() => {
        if (visible) {
            fetchSublines();
            if (station) {
                setName(station.name);
                setDescription(station.description);
                setSubline(station.sublineId);
            } else {
                setName("");
                setDescription("");
                setSubline("");
            }
            setErrors({});
        }
    }, [visible, station]);

    const fetchSublines = async (): Promise<void> => {
        try {
            const response = await SublineApi.GetAllSublines();
            const sublinesMap = response.data.map((subline: any) => ({
                value: subline.id,
                label: subline.name,
            }));
            setSublines(sublinesMap);
        } catch (error) {
            console.error("Erro ao buscar sublinhas: ", error);
        }
    };

    const handleCreate = async (): Promise<void> => {
        let valid = true;
        const newErrors: { name?: string; description?: string } = {};
    
        if (!name.trim()) {
            newErrors.name = "Nome da estação é obrigatório.";
            valid = false;
        } else if (name.length > 300) {
            newErrors.name = "Nome da estação deve ter no máximo 300 caracteres.";
            valid = false;
        }
        if (!description.trim()) {
            newErrors.description = "Descrição da estação é obrigatória.";
            valid = false;
        } else if (description.length > 1000) {
            newErrors.description = "Descrição da estação deve ter no máximo 1000 caracteres.";
            valid = false;
        }
    
        if (!valid) {
            setErrors(newErrors);
            return;
        }
    
        await onCreate(name, description, subline);
        onHide();
    };

    const handleCancel = (): void => {
        if (isDirty) {
            setShowConfirmCancel(true);
        } else {
            onHide();
        }
    };

    return (
        <>
            <Modal
                header={translateForm("station")}
                visible={visible}
                onHide={handleCancel}
                size="md"
                footer={
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
                        <Button
                            onClick={handleCancel}
                            label={translateGeneral("cancel")}
                            severity="secondary"
                            variant="text"
                        />
                        <Button
                            onClick={handleCreate}
                            label={station ? translateGeneral("update") : translateGeneral("register")}
                            icon="save"
                            severity="primary"
                        />
                    </div>
                }
            >
                <div style={{ display: "flex", flexDirection: "column", gap: "1rem", paddingTop: "0.5rem" }}>
                    <Input
                        type="text"
                        label={translateForm("Station")}
                        placeholder={translateForm("StationName")}
                        value={name}
                        onChange={(val: any) => {
                            setName(val);
                            setErrors((prev) => ({ ...prev, name: undefined }));
                        }}
                        required
                        className={errors.name ? "p-invalid" : ""}
                    />
                    {errors.name && <small className="p-error" style={{ color: "var(--color-error-500)" }}>{errors.name}</small>}

                    <Input
                        type="text"
                        label={translateForm("Description")}
                        placeholder={translateForm("StationDescription")}
                        value={description}
                        onChange={(val: any) => {
                            setDescription(val);
                            setErrors((prev) => ({ ...prev, description: undefined }));
                        }}
                        required
                        className={errors.description ? "p-invalid" : ""}
                    />
                    {errors.description && <small className="p-error" style={{ color: "var(--color-error-500)" }}>{errors.description}</small>}

                    <Input
                        type="select"
                        label={translateForm("Subline")}
                        value={subline}
                        options={sublines}
                        onChange={(val: any) => setSubline(val)}
                        placeholder={translateForm("SelectSubline")}
                    />
                </div>
            </Modal>

            <ConfirmModal
                visible={showConfirmCancel}
                onHide={() => setShowConfirmCancel(false)}
                onConfirm={() => {
                    setShowConfirmCancel(false);
                    onHide();
                }}
            />
        </>
    );
};

export default StationDialog;
